/**
 * A CARD'S OWN CHECKLIST — the per-card list of tickable steps (Issue.checklist).
 *
 * Distinct from the board's Definition of Done (the shared list every card ticks
 * through `checks`): this is a list the card authors for itself, different on every
 * card, and ticking its items drives the card's effective progress % ahead of the
 * DoD (src/progress-derive.ts). One component serves both the card's READ view
 * (tick in place, one click, no edit mode) and its EDIT view (add, tick, reorder,
 * inline-edit, delete, and paste-to-items).
 *
 * It owns no persistence: every change is handed up through `onChange`. A structural
 * change (tick, add, delete, reorder) is committed (persist = true); a keystroke in a
 * row updates the draft only (persist = false), so the card is not written on every
 * character — it lands when the field loses focus.
 *
 * SAVE-CONSISTENCY (F1): a row's TEXT edit is staged (persist = false) and committed
 * on blur. On its own that loses the last edit if the card is closed before the field
 * blurs (Escape, click-away, Cancel). So an un-committed text edit is also FLUSHED on
 * unmount: no checklist change — add, tick, delete, reorder, text — is ever lost on any
 * close path, and we still avoid a write per keystroke.
 */
import { useEffect, useRef, useState } from 'react';
import type { ChecklistItem } from '../types';
import InfoTip from './InfoTip';
import Expander from './Expander';

/** A real uuid per item (never the array index), kept through reorders and edits. */
const newId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `cl-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

/** Strip a leading list marker — "1.", "2)", "-", "*", "•" — from a pasted line. */
const stripLead = (s: string): string => s.replace(/^\s*(?:\d+[.)]|[-*•·–—])\s+/, '').trim();

const item = (text: string): ChecklistItem => ({ id: newId(), text, done: false });

export function CardChecklist({
  items,
  editing,
  canEdit,
  onChange,
  autoFocusComposer = false,
  hideHeader = false,
}: {
  items: ChecklistItem[];
  /** true = the card is in edit mode (add/reorder/inline-edit/delete + composer);
   *  false = read mode (tick in place only). */
  editing: boolean;
  /** Whether this viewer may tick / change the list at all. */
  canEdit: boolean;
  /** Hand the new list up. persist = false means "draft only, do not write yet"
   *  (a keystroke); the default true commits it. */
  onChange: (next: ChecklistItem[], persist?: boolean) => void;
  /** Focus the composer on mount — used when the +Checklist chip reveals an empty
   *  list, so the cursor is already where the first item is typed. */
  autoFocusComposer?: boolean;
  /** Drop the component's own "Checklist" head (title + count + bar) — used in the
   *  edit modal, where the collapsible SECTION header already carries the title and
   *  the "N of M done" count. Read mode keeps the head. */
  hideHeader?: boolean;
}) {
  const total = items.length;
  const done = items.reduce((n, it) => n + (it.done ? 1 : 0), 0);
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  const [composer, setComposer] = useState('');
  const [paste, setPaste] = useState<{ lines: string[]; raw: string } | null>(null);
  const composerRef = useRef<HTMLInputElement>(null);
  const rowRefs = useRef(new Map<string, HTMLInputElement>());
  const dragId = useRef<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  // SAVE-CONSISTENCY (F1): the latest list, the latest onChange, and whether a text
  // edit has been staged but not yet committed. Held in refs so the unmount cleanup
  // — which runs once and cannot see a later render's closure — flushes the real
  // pending edit through the real handler.
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const pendingText = useRef(false);

  useEffect(() => {
    if (autoFocusComposer) composerRef.current?.focus();
    // Only on the first reveal; re-focusing on every items change would steal the
    // cursor back from a row the person is editing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Flush a staged text edit on unmount, so closing the card by any path — Save,
  // Cancel, Escape, click-away — can never drop the last keystrokes (the field may
  // never get its blur). A no-op unless a text edit is actually pending.
  useEffect(
    () => () => {
      if (pendingText.current) onChangeRef.current(itemsRef.current, true);
    },
    [],
  );

  const commit = (next: ChecklistItem[]) => {
    pendingText.current = false;
    onChange(next, true);
  };

  const toggle = (id: string) => commit(items.map((it) => (it.id === id ? { ...it, done: !it.done } : it)));

  const setText = (id: string, text: string) => {
    pendingText.current = true;
    onChange(items.map((it) => (it.id === id ? { ...it, text } : it)), false);
  };

  const remove = (id: string) => {
    const idx = items.findIndex((it) => it.id === id);
    const next = items.filter((it) => it.id !== id);
    commit(next);
    // Put the cursor on the previous row (or the composer) so delete-by-keyboard
    // flows without a reach for the mouse.
    const prev = next[idx - 1] ?? next[idx];
    requestAnimationFrame(() => (prev ? rowRefs.current.get(prev.id) : composerRef.current)?.focus());
  };

  const move = (id: string, dir: -1 | 1) => {
    const idx = items.findIndex((it) => it.id === id);
    const to = idx + dir;
    if (idx < 0 || to < 0 || to >= items.length) return;
    const next = items.slice();
    [next[idx], next[to]] = [next[to], next[idx]];
    commit(next);
    requestAnimationFrame(() => rowRefs.current.get(id)?.focus());
  };

  const addFromComposer = () => {
    const text = composer.trim();
    if (!text) return;
    commit([...items, item(text)]);
    setComposer('');
    setPaste(null);
    // Keep the cursor here for the next item — the Trello/monday rhythm.
    composerRef.current?.focus();
  };

  const dropOn = (id: string) => {
    const from = dragId.current;
    dragId.current = null;
    setOverId(null);
    if (!from || from === id) return;
    const fromIdx = items.findIndex((it) => it.id === from);
    const toIdx = items.findIndex((it) => it.id === id);
    if (fromIdx < 0 || toIdx < 0) return;
    const next = items.slice();
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    commit(next);
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text');
    const lines = text.split(/\r?\n/).map((l) => stripLead(l)).filter(Boolean);
    if (lines.length <= 1) return; // a single line pastes normally
    e.preventDefault();
    setComposer(lines[0]);
    setPaste({ lines, raw: text });
  };

  const makeItems = () => {
    if (!paste) return;
    commit([...items, ...paste.lines.map((t) => item(t))]);
    setComposer('');
    setPaste(null);
    composerRef.current?.focus();
  };

  const keepOneLine = () => {
    if (!paste) return;
    setComposer(paste.raw.replace(/\s*\r?\n\s*/g, ' ').trim());
    setPaste(null);
    composerRef.current?.focus();
  };

  // The header — the title, the plain-language distinction from the board DoD (in
  // the InfoTip, never an inline parenthetical, R31), and when there are items the
  // "N of M done" count and a slim bar mirroring the %.
  const header = (
    <div className="cklist-head">
      <span className="cklist-h">
        Checklist
        <InfoTip label="Checklist">
          A list of steps just for this card. The board's Definition of Done is the shared
          list for every card.
        </InfoTip>
      </span>
      {total > 0 && (
        <>
          <span className={`check-count${done === total ? ' is-ready' : ''}`}>
            {done} of {total} done
          </span>
          <span className="cklist-bar" aria-hidden="true">
            <i style={{ width: `${pct}%` }} />
          </span>
        </>
      )}
    </div>
  );

  // READ MODE — tick in place, one click, no edit affordances. The row is the real
  // .check primitive: a native checkbox (R3) and the step's text, done = struck
  // through. Only rendered by the parent when there are items.
  if (!editing) {
    return (
      <div className="cklist">
        {!hideHeader && header}
        <div className="checks">
          {items.map((it) => (
            <label key={it.id} className={`check${it.done ? ' is-done' : ''}`}>
              <input
                type="checkbox"
                checked={it.done}
                disabled={!canEdit}
                onChange={() => toggle(it.id)}
              />
              <span>{it.text}</span>
            </label>
          ))}
        </div>
      </div>
    );
  }

  // EDIT MODE — the same list, each row gaining a drag grip, an inline field and a
  // quiet delete; a composer at the foot; and the paste-to-items offer.
  return (
    <div className="cklist">
      {!hideHeader && header}
      {/* The rows live in an Expander so the FIRST item does not snap into place —
          adding it opens the panel and the list (and the composer below it) glides
          down on the app's standard slide, instead of jumping (Vinnie, 28 Oct). The
          panel rests at auto height, so later items just reflow. */}
      <Expander open={total > 0}>
        <div className="checks">
          {items.map((it) => (
            <div
              key={it.id}
              className={`check edit${it.done ? ' is-done' : ''}${overId === it.id ? ' is-drop' : ''}`}
              onDragOver={(e) => {
                if (dragId.current) {
                  e.preventDefault();
                  if (overId !== it.id) setOverId(it.id);
                }
              }}
              onDrop={() => dropOn(it.id)}
            >
              <span
                className="cklist-grip"
                draggable
                role="button"
                aria-label="Drag to reorder"
                title="Drag to reorder (or Alt+↑/↓)"
                onDragStart={() => (dragId.current = it.id)}
                onDragEnd={() => {
                  dragId.current = null;
                  setOverId(null);
                }}
              >
                ⠿
              </span>
              <input
                type="checkbox"
                checked={it.done}
                disabled={!canEdit}
                aria-label="Done"
                onChange={() => toggle(it.id)}
              />
              <input
                ref={(el) => {
                  if (el) rowRefs.current.set(it.id, el);
                  else rowRefs.current.delete(it.id);
                }}
                className="cklist-text"
                value={it.text}
                disabled={!canEdit}
                onChange={(e) => setText(it.id, e.target.value)}
                onBlur={() => commit(items)}
                onKeyDown={(e) => {
                  if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
                    e.preventDefault();
                    move(it.id, e.key === 'ArrowUp' ? -1 : 1);
                  } else if (e.key === 'Backspace' && it.text === '') {
                    e.preventDefault();
                    remove(it.id);
                  } else if (e.key === 'Enter') {
                    e.preventDefault();
                    composerRef.current?.focus();
                  }
                }}
              />
              {canEdit && (
                <button
                  type="button"
                  className="cklist-del"
                  aria-label="Delete item"
                  title="Delete item"
                  onClick={() => remove(it.id)}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      </Expander>

      {canEdit && (
        <>
          <div className="cklist-add">
            <span className="plus" aria-hidden="true">
              +
            </span>
            <input
              ref={composerRef}
              className="input"
              value={composer}
              placeholder={total > 0 ? 'Add an item — press Enter to add the next' : 'Add the first item — e.g. “Confirm measurement model”'}
              aria-label="Add a checklist item"
              onChange={(e) => setComposer(e.target.value)}
              onPaste={onPaste}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addFromComposer();
                }
              }}
            />
          </div>

          {paste && (
            <div className="cklist-paste">
              <span className="cklist-paste-ic" aria-hidden="true">
                +
              </span>
              <span className="cklist-paste-body">
                You pasted <b>{paste.lines.length} lines</b>. Make them{' '}
                <b>{paste.lines.length} checklist items</b>?
              </span>
              <button type="button" className="btn btn-primary cklist-paste-go" onClick={makeItems}>
                Make {paste.lines.length} items
              </button>
              <button type="button" className="btn cklist-paste-keep" onClick={keepOneLine}>
                Keep as one line
              </button>
              <button
                type="button"
                className="cklist-paste-x"
                aria-label="Dismiss"
                onClick={() => setPaste(null)}
              >
                ×
              </button>
            </div>
          )}

          <div className="cklist-hint">
            {total > 0 || paste ? (
              paste ? (
                <>
                  Leading numbers and bullets (<b>1.</b>, <b>-</b>, <b>•</b>) are stripped
                  automatically. You can tidy any item afterwards.
                </>
              ) : (
                <>
                  Enter adds the item and keeps the cursor for the next. Drag <b>⠿</b> or Alt+↑/↓ to
                  reorder; Backspace on an empty item deletes it.
                </>
              )
            ) : (
              <>
                Break the work into tickable steps. As you tick them off, they move this card's
                progress %.
                <br />
                Pasting a numbered or bulleted list offers to turn each line into an item.
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default CardChecklist;
